/**
 * voronoiGlassPipeline — voronoi cells with translucent colored glass.
 *
 * The screen is divided into voronoi cells (one per "tile"). Each
 * cell is colored with a translucent hue (warm orange/red/yellow).
 * Black borders separate cells. Behind the cells, a soft light
 * shines through, creating the "stained glass" effect. Light
 * patterns animate behind the cells.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface VoronoiGlassPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

vec2 hash22(vec2 p) {
  p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
  return fract(sin(p) * 43758.5453);
}

// Returns: (cellId.x, cellId.y, distance-to-cell-center, distance-to-nearest-edge).
vec4 voronoi(vec2 p) {
  vec2 ip = floor(p);
  vec2 fp = fract(p);
  vec2 nearestCell = vec2(0.0);
  vec2 cellOffset = vec2(0.0);
  float minDist = 100.0;
  for (int j = -1; j <= 1; j++) {
    for (int i = -1; i <= 1; i++) {
      vec2 offset = vec2(float(i), float(j));
      vec2 sitePos = offset + hash22(ip + offset);
      float d = length(fp - sitePos);
      if (d < minDist) {
        minDist = d;
        nearestCell = ip + offset;
        cellOffset = sitePos;
      }
    }
  }
  // Distance to nearest edge (second-nearest cell).
  float minEdgeDist = 100.0;
  for (int j = -2; j <= 2; j++) {
    for (int i = -2; i <= 2; i++) {
      vec2 offset = vec2(float(i), float(j));
      vec2 sitePos = offset + hash22(ip + offset);
      float d = length(fp - sitePos);
      if (d > minDist && d < minEdgeDist) {
        minEdgeDist = d;
      }
    }
  }
  return vec4(nearestCell, minDist, minEdgeDist);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected coords, scaled to give us ~10x10 cells.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  vec2 cellP = p * 8.0;
  vec4 v = voronoi(cellP);
  vec2 cellId = v.xy;
  float dist = v.z;
  float edgeDist = v.w;

  // Color per cell: based on cellId hash + time.
  float h = hash21(cellId);
  float hue = fract(h + uTime * 0.05);
  // Warm palette: red, orange, yellow, gold, magenta.
  vec3 c1 = vec3(0.95, 0.30, 0.10);
  vec3 c2 = vec3(0.95, 0.65, 0.15);
  vec3 c3 = vec3(0.85, 0.20, 0.40);
  vec3 c4 = vec3(0.65, 0.85, 0.30);
  vec3 cellColor;
  if (hue < 0.25) cellColor = c1;
  else if (hue < 0.5) cellColor = c2;
  else if (hue < 0.75) cellColor = c3;
  else cellColor = c4;
  // Audio: brighten with mid.
  cellColor *= 1.0 + mid * 0.4;

  // Cell content: gradient from the cell center.
  float cellGlow = exp(-dist * dist * 4.0);

  // Border: black with smooth edge.
  float border = smoothstep(0.0, 0.05, edgeDist);

  // Light shining through from behind: a moving radial gradient.
  vec2 lightCenter = vec2(sin(uTime * 0.4) * 0.6, cos(uTime * 0.3) * 0.4);
  float lightDist = length(p - lightCenter);
  float lightIntensity = exp(-lightDist * lightDist * 1.5);
  lightIntensity *= 0.5 + bass * 0.8;

  // Cell color modulated by light + cell glow.
  vec3 cellFinal = cellColor * (0.5 + lightIntensity * 0.8) * (0.6 + cellGlow * 0.6);

  // Audio: high adds sparkle.
  cellFinal += vec3(1.0, 0.9, 0.7) * high * cellGlow * 0.4;

  // Black background between cells.
  vec3 bg = vec3(0.02, 0.02, 0.04);
  vec3 col = mix(bg, cellFinal, border);

  // Vignette.
  float vig = 1.0 - dot(p, p) * 0.3;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeVoronoiGlassBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeVoronoiGlassRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createVoronoiGlassPipeline(gl: WebGL2RenderingContext): VoronoiGlassPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawVoronoiGlass(
  gl: WebGL2RenderingContext,
  pipe: VoronoiGlassPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
